/*
 * Low-precision planetary position calculator.
 * Based on the well-known public-domain method by Paul Schlyter
 * ("How to compute planetary positions", stjarnhimlen.se/comp/ppcomp.html)
 * Accurate to a fraction of a degree for the classical planets and a few
 * arcminutes for the Moon (with main perturbation terms included) --
 * more than enough precision to know which zodiac sign/degree a body is in.
 * No network calls, no external data: this runs entirely client-side.
 */

const D2R = Math.PI / 180;
const R2D = 180 / Math.PI;

function sinD(deg) { return Math.sin(deg * D2R); }
function cosD(deg) { return Math.cos(deg * D2R); }
function atan2D(y, x) { return Math.atan2(y, x) * R2D; }
function norm360(deg) { let d = deg % 360; if (d < 0) d += 360; return d; }

// days since 2000 Jan 0.0 UT (matches Schlyter's "d" convention)
function daysSinceEpoch(date) {
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth() + 1;
  const day = date.getUTCDate() +
    (date.getUTCHours() + date.getUTCMinutes() / 60 + date.getUTCSeconds() / 3600) / 24;
  const d = 367 * y - Math.floor(7 * (y + Math.floor((m + 9) / 12)) / 4) +
    Math.floor(275 * m / 9) + day - 730530;
  return d;
}

// Orbital elements at J2000 plus per-day rates: [N, i, w, a, e, M] each as {c, r} (constant + rate*d)
const ELEMENTS = {
  sun:     { N: [0, 0], i: [0, 0], w: [282.9404, 4.70935e-5], a: [1.000000, 0], e: [0.016709, -1.151e-9], M: [356.0470, 0.9856002585] },
  moon:    { N: [125.1228, -0.0529538083], i: [5.1454, 0], w: [318.0634, 0.1643573223], a: [60.2666, 0], e: [0.054900, 0], M: [115.3654, 13.0649929509] },
  mercury: { N: [48.3313, 3.24587e-5], i: [7.0047, 5.00e-8], w: [29.1241, 1.01444e-5], a: [0.387098, 0], e: [0.205635, 5.59e-10], M: [168.6562, 4.0923344368] },
  venus:   { N: [76.6799, 2.46590e-5], i: [3.3946, 2.75e-8], w: [54.8910, 1.38374e-5], a: [0.723330, 0], e: [0.006773, -1.302e-9], M: [48.0052, 1.6021302244] },
  mars:    { N: [49.5574, 2.11081e-5], i: [1.8497, -1.78e-8], w: [286.5016, 2.92961e-5], a: [1.523688, 0], e: [0.093405, 2.516e-9], M: [18.6021, 0.5240207766] },
  jupiter: { N: [100.4542, 2.76854e-5], i: [1.3030, -1.557e-7], w: [273.8777, 1.64505e-5], a: [5.20256, 0], e: [0.048498, 4.469e-9], M: [19.8950, 0.0830853001] },
  saturn:  { N: [113.6634, 2.38980e-5], i: [2.4886, -1.081e-7], w: [339.3939, 2.97661e-5], a: [9.55475, 0], e: [0.055546, -9.499e-9], M: [316.9670, 0.0334442282] },
  uranus:  { N: [74.0005, 1.3978e-5], i: [0.7733, 1.9e-8], w: [96.6612, 3.0565e-5], a: [19.18171, -1.55e-8], e: [0.047318, 7.45e-9], M: [142.5905, 0.011725806] },
  neptune: { N: [131.7806, 3.0173e-5], i: [1.7700, -2.55e-7], w: [272.8461, -6.027e-6], a: [30.05826, 3.313e-8], e: [0.008606, 2.15e-9], M: [260.2471, 0.005995147] },
  // Pluto: simple mean-element approximation (adequate for sign/degree display, ~248yr period)
  pluto:   { N: [110.30, 0], i: [17.16, 0], w: [113.83, 0], a: [39.482, 0], e: [0.2488, 0], M: [14.882, 0.0039793] }
};

function elementAt(pair, d) { return pair[0] + pair[1] * d; }

function keplerSolve(M, e) {
  // one Newton-ish correction pass (sufficient for e < 0.25 at this precision)
  let E = M + (e * R2D) * sinD(M) * (1 + e * cosD(M));
  for (let iter = 0; iter < 3; iter++) {
    const dM = M - (E - e * R2D * sinD(E));
    const dE = dM / (1 - e * cosD(E));
    E += dE;
  }
  return E;
}

function planetHeliocentric(name, d) {
  const el = ELEMENTS[name];
  const N = norm360(elementAt(el.N, d));
  const i = elementAt(el.i, d);
  const w = norm360(elementAt(el.w, d));
  const a = elementAt(el.a, d);
  const e = elementAt(el.e, d);
  const M = norm360(elementAt(el.M, d));

  const E = keplerSolve(M, e);
  const xv = a * (cosD(E) - e);
  const yv = a * (Math.sqrt(1 - e * e) * sinD(E));
  const v = atan2D(yv, xv);
  const r = Math.sqrt(xv * xv + yv * yv);

  const vw = v + w;
  const xh = r * (cosD(N) * cosD(vw) - sinD(N) * sinD(vw) * cosD(i));
  const yh = r * (sinD(N) * cosD(vw) + cosD(N) * sinD(vw) * cosD(i));
  const zh = r * (sinD(vw) * sinD(i));
  return { xh, yh, zh, r, N, w, M };
}

function moonPerturbations(d, moon, sunM, sunW) {
  const Ms = norm360(sunM);
  const Mm = norm360(moon.M);
  const Nm = moon.N;
  const Lm = norm360(Nm + moon.w + Mm);
  const Ls = norm360(sunM + sunW);
  const D = norm360(Lm - Ls);
  const F = norm360(Lm - Nm);

  let dLon = 0;
  dLon += -1.274 * sinD(Mm - 2 * D);
  dLon += 0.658 * sinD(2 * D);
  dLon += -0.186 * sinD(Ms);
  dLon += -0.059 * sinD(2 * Mm - 2 * D);
  dLon += -0.057 * sinD(Mm - 2 * D + Ms);
  dLon += 0.053 * sinD(Mm + 2 * D);
  dLon += 0.046 * sinD(2 * D - Ms);
  dLon += 0.041 * sinD(Mm - Ms);
  dLon += -0.035 * sinD(D);
  dLon += -0.031 * sinD(Mm + Ms);
  dLon += -0.015 * sinD(2 * F - 2 * D);
  dLon += 0.011 * sinD(Mm - 4 * D);
  return dLon;
}

// NOTE on reference frame: Schlyter's orbital elements (N, i, w, M and their per-day rates)
// already yield the ecliptic longitude referred to the *equinox and ecliptic of date* -- no
// separate precession correction is needed on top of them. This was verified numerically
// against pyephem's of-date apparent geocentric positions (converted RA/Dec -> ecliptic using
// the of-date mean obliquity): agreement was within ~0.01-0.27 degrees for all ten bodies
// (worst case Pluto/Saturn, which use simplified elements), confirming the raw output of
// planetHeliocentric()/computePositions() below is already "tropical, of date".

// Lahiri (Chitrapaksha) ayanamsa: offset between the tropical (equinox-based) zodiac and the
// sidereal (star-based) zodiac used in Vedic/Jyotish astrology. Its value grows at very nearly
// the general-precession rate (~50.29"/yr = 0.0139697 deg/yr). Anchor value taken at
// 2000-01-01: 23°51'11" = 23.8531 deg.
const LAHIRI_AYANAMSA_AT_2000 = 23.8531;
const AYANAMSA_DEG_PER_YEAR = 0.0139697;

function lahiriAyanamsa(d) {
  const years = d / 365.25;
  return LAHIRI_AYANAMSA_AT_2000 + AYANAMSA_DEG_PER_YEAR * years;
}

function computePositions(date) {
  const d = daysSinceEpoch(date);
  const ayanamsa = lahiriAyanamsa(d);

  const sun = planetHeliocentric('sun', d);
  // Sun's own heliocentric coords ARE geocentric coords of the Sun (Earth at origin)
  const sunLon = norm360(atan2D(sun.yh, sun.xh));
  const xs = sun.xh, ys = sun.yh; // Sun geocentric ecliptic rectangular (z ~ 0)

  const results = {};

  function record(name, tropical) {
    const sidereal = norm360(tropical - ayanamsa); // Lahiri sidereal (Vedic) longitude
    results[name] = { tropical, sidereal };
  }

  record('sun', sunLon);

  // Moon: geocentric directly, elements already Earth-centered
  const moon = planetHeliocentric('moon', d);
  let moonLon = norm360(atan2D(moon.yh, moon.xh));
  moonLon = norm360(moonLon + moonPerturbations(d, moon, ELEMENTS.sun ? elementAt(ELEMENTS.sun.M, d) : 0, elementAt(ELEMENTS.sun.w, d)));
  record('moon', moonLon);

  // Rahu (mean lunar ascending node) / Ketu (descending node, always 180 deg opposite Rahu).
  // moon.N is the Moon's ascending-node longitude, already computed above as part of the
  // Moon's orbital elements -- cross-checked against Meeus's standard mean-node formula
  // (Omega = 125.0445 - 1934.1362891*T deg, T in Julian centuries since J2000): the rates
  // match to 6 significant figures and the constants agree to ~0.001 deg once the ~1.5 day
  // epoch offset between the two conventions is accounted for.
  const rahuLon = norm360(moon.N);
  const ketuLon = norm360(rahuLon + 180);
  record('rahu', rahuLon);
  record('ketu', ketuLon);

  ['mercury', 'venus', 'mars', 'jupiter', 'saturn', 'uranus', 'neptune', 'pluto'].forEach((name) => {
    const p = planetHeliocentric(name, d);
    const xg = p.xh + xs;
    const yg = p.yh + ys;
    const lon = norm360(atan2D(yg, xg));
    record(name, lon);
  });

  return { bodies: results, ayanamsa };
}

const SIGNS = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];

function lonToSignDegree(lon) {
  const signIndex = Math.floor(lon / 30);
  const deg = lon - signIndex * 30;
  const wholeDeg = Math.floor(deg);
  const minutes = Math.floor((deg - wholeDeg) * 60);
  return { sign: SIGNS[signIndex], deg: wholeDeg, min: minutes };
}

if (typeof module !== 'undefined') {
  module.exports = { computePositions, lonToSignDegree, daysSinceEpoch, SIGNS };
}
